import type { OcrToken } from "./types";

export interface CompoundCandidate {
  start: number;
  end: number;
  expression: string;
}

const nonLexicalCategories = new Set(["助詞", "助動詞", "補助記号"]);
const expressionBreakCategories = new Set(["補助記号"]);

export function compoundCandidates(
  tokenItems: Array<{ token: OcrToken; sourceIndex: number }>,
  maxTokens = 4,
  maxCandidates = 32
): CompoundCandidate[] {
  const candidates: CompoundCandidate[] = [];
  for (let start = 0; start < tokenItems.length; start += 1) {
    if (!isLexical(tokenItems[start].token)) continue;
    for (let end = start + 1; end < tokenItems.length && end < start + maxTokens; end += 1) {
      const previous = tokenItems[end - 1];
      const current = tokenItems[end];
      if (previous.sourceIndex + 1 !== current.sourceIndex || !isLexical(current.token)) break;
      candidates.push({
        start,
        end,
        expression: tokenItems.slice(start, end + 1).map(({ token }) => token.surface).join("")
      });
      if (candidates.length >= maxCandidates) return candidates;
    }
  }
  return candidates;
}

/**
 * Offer every bounded contiguous token span to the dictionary, including spans
 * that cross particles. Sudachi deliberately exposes the internal morphemes of
 * expressions such as なんでもかんでも; JMdict is the authority for whether
 * those morphemes should also be presented as one reader lookup unit.
 */
export function readerExpressionCandidates(
  tokenItems: Array<{ token: OcrToken; sourceIndex: number }>,
  maxTokens = 8,
  maxCandidates = 128
): CompoundCandidate[] {
  const candidates: CompoundCandidate[] = [];
  for (let start = 0; start < tokenItems.length; start += 1) {
    if (isExpressionBreak(tokenItems[start].token)) continue;
    for (let end = start + 1; end < tokenItems.length && end < start + maxTokens; end += 1) {
      const previous = tokenItems[end - 1];
      const current = tokenItems[end];
      if (previous.sourceIndex + 1 !== current.sourceIndex || isExpressionBreak(current.token)) break;
      candidates.push({
        start,
        end,
        expression: tokenItems.slice(start, end + 1).map(({ token }) => token.surface).join("")
      });
      if (candidates.length >= maxCandidates) return candidates;
    }
  }
  return candidates;
}

export function selectCompoundSpans(
  candidates: CompoundCandidate[],
  matchedExpressions: ReadonlySet<string>
): CompoundCandidate[] {
  const matches = candidates
    .filter(({ expression }) => matchedExpressions.has(expression))
    .sort((left, right) => (right.end - right.start) - (left.end - left.start) || left.start - right.start);
  const occupied = new Set<number>();
  const selected: CompoundCandidate[] = [];
  for (const candidate of matches) {
    const indexes = Array.from({ length: candidate.end - candidate.start + 1 }, (_, offset) => candidate.start + offset);
    if (indexes.some((index) => occupied.has(index))) continue;
    indexes.forEach((index) => occupied.add(index));
    selected.push(candidate);
  }
  return selected.sort((left, right) => left.start - right.start);
}

function isLexical(token: OcrToken): boolean {
  return !nonLexicalCategories.has(token.partOfSpeech[0] ?? "");
}

function isExpressionBreak(token: OcrToken): boolean {
  return expressionBreakCategories.has(token.partOfSpeech[0] ?? "");
}
